import React from 'react';
import type { ContactInfoComponent as ContactInfoComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';

interface ContactInfoComponentProps {
  component: ContactInfoComponentType;
}

export const ContactInfoComponent: React.FC<ContactInfoComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const { data } = component;

  // Color priority: customColors (user explicit) > theme/brand package > stored data defaults
  const backgroundColor =
    (data as any).customColors?.background ||
    themeStyles.contactInfo?.background ||
    data.backgroundColor ||
    '#ffffff';
  const textColor =
    (data as any).customColors?.text ||
    themeStyles.contactInfo?.textColor ||
    data.textColor ||
    '#1f2937';
  const accentColor =
    (data as any).customColors?.accent ||
    data.accentColor ||
    themeStyles.contactInfo?.accentColor ||
    '#3b82f6';
  const titleColor =
    (data as any).customColors?.title || themeStyles.contactInfo?.titleColor || textColor;

  //Helper function to validate and convert Google Maps URLs to embed format.
  const getValidMapEmbedUrl = (url: string | undefined): string | null => {
    if (!url) return null;
    //Already a valid embed URL, return as is.
    if (url.includes('google.com/maps/embed')) {
      return url;
    }
    // Check if it's a regular Google Maps URL that can be converted
    // Common formats:
    // - https://www.google.com/maps/place/...
    // - https://maps.google.com/...
    // - https://goo.gl/maps/...
    // - https://maps.app.goo.gl/...
    if (
      url.includes('google.com/maps') ||
      url.includes('maps.google.com') ||
      url.includes('goo.gl/maps') ||
      url.includes('maps.app.goo.gl')
    ) {
      return null;
    }

    return url;
  };

  const validMapEmbedUrl = getValidMapEmbedUrl(data.mapEmbedUrl);

  // Determine if map should actually render (for layout purposes)
  const shouldShowMap = data.showMap && (validMapEmbedUrl || data.mapEmbedUrl);

  // Extract horizontalPadding with default
  const horizontalPadding = (data as any).horizontalPadding || 'md';

  // Get horizontal padding classes for container
  const getHorizontalPaddingClasses = () => {
    const paddingMap: Record<string, string> = {
      none: 'px-0',
      xs: 'px-2',
      sm: 'px-4',
      md: 'px-4 sm:px-6 lg:px-8',
      lg: 'px-6 sm:px-8 lg:px-12',
      xl: 'px-8 sm:px-12 lg:px-16',
      '2xl': 'px-12 sm:px-16 lg:px-24',
    };
    return paddingMap[horizontalPadding] || paddingMap.md;
  };

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.contactInfo?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.contactInfo?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    const buttons = (data as any).buttons;
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap justify-center gap-4 mt-12">
        {buttons.map((button: any, index: number) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor }}>
      <div className={`max-w-7xl mx-auto ${getHorizontalPaddingClasses()}`}>
        {/* Header */}
        {(data.title || data.subtitle || data.description) && (
          <div className="text-center mb-12">
            {data.title && (
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: titleColor, ...headingFontStyle }}
              >
                {data.title}
              </h2>
            )}
            {data.subtitle && (
              <p className="text-2xl mb-4" style={{ color: accentColor, ...bodyFontStyle }}>
                {data.subtitle}
              </p>
            )}
            {data.description && (
              <p className="text-lg" style={{ color: textColor, ...bodyFontStyle }}>
                {data.description}
              </p>
            )}
          </div>
        )}

        {/* Two Column Layout (single column when no map) */}
        <div
          className={`grid grid-cols-1 ${shouldShowMap ? 'lg:grid-cols-2' : ''} gap-8 ${
            data.layout === 'map-left' ? 'lg:flex-row-reverse' : ''
          }`}
        >
          {/* Contact Info Column */}
          <div
            className={`${data.layout === 'map-left' ? 'lg:order-2' : 'lg:order-1'} ${!shouldShowMap ? 'mx-auto max-w-xl' : ''}`}
          >
            <div className="space-y-6">
              {/* Business Name */}
              {data.businessName && (
                <div>
                  <h3
                    className="text-2xl font-semibold mb-4"
                    style={{ color: titleColor, ...headingFontStyle }}
                  >
                    {data.businessName}
                  </h3>
                </div>
              )}

              {/* Address */}
              {data.address && (
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <MapPin size={24} style={{ color: accentColor }} />
                  </div>
                  <div>
                    <p className="text-lg" style={{ color: textColor, ...bodyFontStyle }}>
                      {data.address}
                    </p>
                  </div>
                </div>
              )}

              {/* Phone */}
              {data.phone && (
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <Phone size={24} style={{ color: accentColor }} />
                  </div>
                  <div>
                    <a
                      href={`tel:${data.phone.replace(/[^0-9+]/g, '')}`}
                      className="text-lg hover:underline"
                      style={{ color: textColor, ...bodyFontStyle }}
                    >
                      {data.phone}
                    </a>
                  </div>
                </div>
              )}

              {/* Email */}
              {data.email && (
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <Mail size={24} style={{ color: accentColor }} />
                  </div>
                  <div>
                    <a
                      href={`mailto:${data.email}`}
                      className="text-lg hover:underline"
                      style={{ color: textColor, ...bodyFontStyle }}
                    >
                      {data.email}
                    </a>
                  </div>
                </div>
              )}

              {/* Hours */}
              {data.hours && (
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <Clock size={24} style={{ color: accentColor }} />
                  </div>
                  <div>
                    <h4
                      className="text-lg font-semibold mb-2"
                      style={{ color: titleColor, ...headingFontStyle }}
                    >
                      Hours
                    </h4>
                    <p
                      className="whitespace-pre-line"
                      style={{ color: textColor, ...bodyFontStyle }}
                    >
                      {data.hours}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Map Column */}
          {data.showMap && validMapEmbedUrl && (
            <div className={`${data.layout === 'map-left' ? 'lg:order-1' : 'lg:order-2'}`}>
              <div className="w-full h-full min-h-[400px] rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src={validMapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location Map"
                />
              </div>
            </div>
          )}{' '}
          {/* Error message if map URL is invalid */}
          {data.showMap && data.mapEmbedUrl && !validMapEmbedUrl && (
            <div className={`${data.layout === 'map-left' ? 'lg:order-1' : 'lg:order-2'}`}>
              <div className="w-full h-full min-h-[400px] rounded-lg border-2 border-red-300 bg-red-50 p-8 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-red-800 font-semibold mb-2">Invalid Google Maps URL</p>
                  <p className="text-red-600 text-sm">
                    Please use a Google Maps embed URL. To get the embed URL:
                  </p>
                  <ol className="text-red-600 text-sm text-left mt-2 list-decimal list-inside space-y-1">
                    <li>Go to Google Maps and search for your location</li>
                    <li>Click "Share" → "Embed a map"</li>
                    <li>Copy the URL from the iframe src attribute</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Call-to-Action Buttons */}
        {renderButtons()}
      </div>
    </section>
  );
};
